import React, { useState } from 'react';
import { Card, Bold, Flex, Title } from './TremorCompat';
import { Input, InputGroup, InputPicker, IconButton } from 'rsuite';
import { MdOutlineInfo as IconInfo, MdOutlineAdd as IconPlus, MdOutlineClose as IconRemove } from 'react-icons/md';
import { DisplayItem } from './DisplayItems';
import { COLOURS } from '../styles';
import { devLog } from '../utils/devLog';
import { readStoredUrls, validateFirmwareUrl, normaliseFirmwareUrl } from '../utils/firmwareUrlStore';
import { MiddleEllipsis } from './MiddleEllipsis';

/**
 * Shared firmware-upgrade card: title, a firmware-URL picker with a visible
 * "Add URL" affordance, an optional in-progress status area, and a caller-
 * supplied actions area (Upgrade button(s), main/backup toggle, etc.).
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string[]} props.urls               list of firmware URLs to choose from
 * @param {string} [props.urlKey]             firmwareUrlStore category, enables removal
 * @param {string} [props.selectedFileName]   currently-selected URL
 * @param {(value: string) => void} props.onSelect   called when a URL is chosen
 * @param {(value: string) => void} props.onAddUrl   called to persist+add a new URL
 * @param {(key: string, value: string) => void} [props.onRemoveUrl] drop a user-added URL
 * @param {boolean} [props.showProgress]      render the progress rows
 * @param {Array<any>} [props.progressRegisters]  resolved register-data objects to show while in progress
 * @param {React.ReactNode} props.children    actions area (buttons / toggle)
 */
export const FirmwareUpgradeCard = ({
  title,
  urls = [],
  urlKey,
  selectedFileName,
  onSelect,
  onAddUrl,
  onRemoveUrl,
  showProgress = false,
  progressRegisters = [],
  children,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [addError, setAddError] = useState('');
  // Only URLs the user added are removable — built-in defaults live in code and
  // loadUrls() would merge them straight back in on the next load.
  const [removable, setRemovable] = useState(() => (urlKey ? readStoredUrls(urlKey) : []));

  // Re-read when the category changes. A useState initialiser runs once per
  // mount, so a card instance reused for a different urlKey would keep the
  // previous category's stored list and decide the ✕ buttons from it — showing
  // one on an entry that is a built-in default here, and none on a URL the user
  // actually added. Nothing triggers that today: urlKey is fixed per device
  // family, and switching device unmounts DeviceView via showDevice. It is one
  // in-view device switcher away from being live, and the initialiser makes the
  // assumption invisible.
  //
  // Adjusted during render rather than in an effect — React's documented
  // pattern for state derived from props. An effect would commit the stale list
  // first and re-render, and would fire on every mount since a fresh array is
  // never Object.is-equal to the initialiser's.
  const [lastUrlKey, setLastUrlKey] = useState(urlKey);
  if (urlKey !== lastUrlKey) {
    setLastUrlKey(urlKey);
    setRemovable(urlKey ? readStoredUrls(urlKey) : []);
  }

  const handleAdd = () => {
    const value = normaliseFirmwareUrl(newUrl);
    const { valid, reason } = validateFirmwareUrl(value);
    if (!valid) {
      setAddError(reason);
      return;
    }
    // Already offered by this picker — just select it. Persisting a copy would
    // store a built-in default as though the user had added it, which then
    // shows a ✕ on an entry the "defaults aren't removable" rule says should
    // not have one. `urls` is defaults + stored, so this covers both.
    if (urls.includes(value)) {
      devLog('FirmwareUpgradeCard URL already listed, selecting', value);
      onSelect(value);
      setNewUrl('');
      setAddError('');
      return;
    }

    devLog('FirmwareUpgradeCard add URL', value);
    onAddUrl(value);

    // Only arm what actually persisted — same rule as handleRemove. If the
    // write failed (quota, storage disabled) the URL isn't in `urls` either,
    // so selecting it would leave the picker blank with Upgrade enabled for a
    // selection the user can't see: exactly the hidden-armed-URL bug this card
    // exists to prevent.
    const stored = urlKey ? readStoredUrls(urlKey) : null;
    if (stored && !stored.includes(value)) {
      setAddError('Could not save that URL — check browser storage and try again.');
      return;
    }

    onSelect(value);
    setNewUrl('');
    setAddError('');
    if (stored) setRemovable(stored);
  };

  const handleChange = (value) => {
    setNewUrl(value);
    if (addError) setAddError('');
  };

  const handleRemove = (url) => {
    if (!urlKey || !onRemoveUrl) return;
    devLog('FirmwareUpgradeCard remove URL', urlKey, url);
    onRemoveUrl(urlKey, url);
    // Re-read rather than assume it worked: the write can fail (quota, storage
    // disabled) and the parent can refuse outright — built-in defaults aren't
    // removable. Filtering blind would drop the ✕ so the user couldn't retry
    // without a reload, and would disarm a URL that is in fact still there.
    const stillStored = readStoredUrls(urlKey);
    setRemovable(stillStored);
    if (selectedFileName === url && !stillStored.includes(url)) onSelect('');
  };

  const canRemove = Boolean(urlKey && onRemoveUrl);

  // Built-in defaults are shown unfiltered, so an invalid one reaches the
  // picker as an ordinary entry — and the Upgrade button, which applies the
  // same rule, then sits disabled with nothing explaining why. Say why instead
  // of hiding the entry: a bad default is a code defect and should be visible.
  const selectionCheck = selectedFileName ? validateFirmwareUrl(selectedFileName) : { valid: true };
  const selectionError = selectionCheck.valid ? '' : selectionCheck.reason;

  return (
    <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3 space-y-2">
      <Flex justifyContent="center" className="space-x-4 ">
        <Title>
          <Bold>{title}</Bold>
        </Title>
      </Flex>

      <Flex justifyContent="between" className="space-x-4 ">
        {/* Deliberately not `creatable`. Its onCreate handed the raw typed
            string straight to onAddUrl, bypassing validateFirmwareUrl, and
            rsuite selects a created value whether or not we accept it — so
            rejecting one there would still leave it armed in the picker. The
            "Add firmware URL" field below is the single validated way in. */}
        <InputPicker
          data={urls.map((item) => ({ label: item, value: item }))}
          style={{ width: '100%' }}
          value={selectedFileName}
          onSelect={onSelect}
          renderValue={(value) => <MiddleEllipsis text={value} />}
          renderMenuItem={(label, item) => (
            <Flex justifyContent="between" className="space-x-2">
              <MiddleEllipsis text={label} style={{ flex: '1 1 auto' }} />
              {canRemove && removable.includes(item.value) && (
                <IconButton
                  size="xs"
                  appearance="subtle"
                  icon={<IconRemove />}
                  aria-label={`Remove ${item.value}`}
                  onClick={(event) => {
                    // Keep the click from selecting the row we're deleting.
                    event.stopPropagation();
                    handleRemove(item.value);
                  }}
                />
              )}
            </Flex>
          )}
        />
      </Flex>

      {/* Visible "add firmware URL" affordance */}
      <InputGroup size="xs" style={{ width: '100%' }}>
        {/* An <input> holds live editable text, so it can't be middle-ellipsised
            — the browser scrolls it and CSS can only cut the tail when unfocused.
            The title at least surfaces the whole value on hover. */}
        <Input
          placeholder="Add firmware URL (http(s)://…/firmware.bin)"
          value={newUrl}
          title={newUrl || undefined}
          onChange={handleChange}
          onPressEnter={handleAdd}
        />
        <InputGroup.Button onClick={handleAdd} aria-label={`Add firmware URL for ${title}`}>
          <IconPlus />
        </InputGroup.Button>
      </InputGroup>
      {addError && (
        <div role="alert" style={{ color: '#f04438', fontSize: '0.75rem' }}>
          {addError}
        </div>
      )}
      {!addError && selectionError && (
        <div role="alert" style={{ color: '#f04438', fontSize: '0.75rem' }}>
          {`Cannot upgrade with the selected URL — ${selectionError.replace(/^Firmware URLs /, 'firmware URLs ')}`}
        </div>
      )}

      {showProgress && (
        <div>
          {progressRegisters.filter(Boolean).map((register, idx) => (
            <DisplayItem key={register.address ?? idx} icon={IconInfo} register={register} borderOff={true} />
          ))}
        </div>
      )}

      <Flex justifyContent="center" className="space-x-4 ">
        {children}
      </Flex>
    </Card>
  );
};
